import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Bot, User, Send, Loader2, Trash2, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronRight, Database, FileText, Wrench, Code2,
  Info, RefreshCw, AlertCircle, BookOpen, Scissors, Users, Clock,
  FlaskConical, Eye, EyeOff, MessageSquare, XCircle, Flag,
  X, PanelRight,
} from "lucide-react";

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  debugOutput?: DebugOutput;
  flagged?: boolean;
  flagReason?: string;
}

interface DebugOutput {
  systemPrompt: string;
  sources: Array<{
    id: string;
    label: string;
    type: string;
    excerpt: string;
    used: boolean;
  }>;
  toolCalls: Array<{
    name: string;
    args: Record<string, any>;
    result: string;
    durationMs: number;
  }>;
  processingMs: number;
  iterations: number;
  hallucinationWarnings: Array<{
    type: string;
    description: string;
    severity: "high" | "medium" | "low";
  }>;
  diagnosis: string;
  salonData: {
    services: Array<{ id: number; name: string; price: string; durationMinutes: number }>;
    professionals: Array<{ id: number; name: string }>;
    knowledgeDocs: Array<{ id: number; name: string; status: string }>;
    hasFaq: boolean;
  };
  pendingAvailabilityOffer?: {
    salonId: number;
    serviceIds: number[];
    date: string;
    options: Array<{ time: string; professionalId: number }>;
    expiresAt: string;
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function severityColor(severity: "high" | "medium" | "low") {
  if (severity === "high") return "bg-red-500/15 text-red-600 border-red-500/30";
  if (severity === "medium") return "bg-amber-500/15 text-amber-600 border-amber-500/30";
  return "bg-blue-500/15 text-blue-500 border-blue-500/30";
}

function sourceTypeIcon(type: string) {
  if (type === "salon_data") return <Database className="w-3.5 h-3.5 text-blue-500" />;
  if (type === "services") return <Scissors className="w-3.5 h-3.5 text-purple-500" />;
  if (type === "professionals") return <Users className="w-3.5 h-3.5 text-green-500" />;
  if (type === "knowledge_doc") return <FileText className="w-3.5 h-3.5 text-amber-500" />;
  if (type === "faq") return <BookOpen className="w-3.5 h-3.5 text-pink-500" />;
  if (type === "tool_call") return <Wrench className="w-3.5 h-3.5 text-orange-500" />;
  if (type === "client_history") return <Clock className="w-3.5 h-3.5 text-cyan-500" />;
  if (type === "weather") return <Info className="w-3.5 h-3.5 text-sky-500" />;
  return <Database className="w-3.5 h-3.5 text-muted-foreground" />;
}

function toolCallColor(name: string) {
  if (name === "check_availability") return "bg-blue-500/10 border-blue-500/30 text-blue-600";
  if (name === "create_appointment") return "bg-green-500/10 border-green-500/30 text-green-600";
  if (name === "cancel_appointment") return "bg-red-500/10 border-red-500/30 text-red-600";
  if (name === "get_appointments_by_phone") return "bg-purple-500/10 border-purple-500/30 text-purple-600";
  if (name === "check_weather") return "bg-sky-500/10 border-sky-500/30 text-sky-600";
  return "bg-muted border-border text-muted-foreground";
}

// ─── Componente de mensagem do chat ──────────────────────────────────────────

function ChatBubble({
  msg,
  onFlag,
  onUnflag,
  showInternalMetadata,
}: {
  msg: ChatMessage;
  onFlag: (id: string, reason: string) => void;
  onUnflag: (id: string) => void;
  showInternalMetadata: boolean;
}) {
  const [showFlagForm, setShowFlagForm] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const isUser = msg.role === "user";
  const hasWarnings = (msg.debugOutput?.hallucinationWarnings?.length ?? 0) > 0;
  const highWarnings = msg.debugOutput?.hallucinationWarnings?.filter((w) => w.severity === "high") ?? [];

  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      {/* Avatar */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isUser ? "bg-primary text-primary-foreground" : "bg-secondary border border-border/50"}`}>
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4 text-primary" />}
      </div>

      {/* Conteúdo */}
      <div className={`flex flex-col gap-1 max-w-[80%] ${isUser ? "items-end" : "items-start"}`}>
        {/* Balão */}
        <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-primary text-primary-foreground rounded-tr-sm"
            : `bg-card border border-border/50 text-foreground rounded-tl-sm ${msg.flagged ? "border-red-500/50 bg-red-500/5" : ""}`
        }`}>
          {msg.content}
        </div>

        {/* Indicadores de aviso (apenas para respostas do agente) */}
        {!isUser && showInternalMetadata && (
          <div className="flex items-center gap-2 flex-wrap">
            {/* Timestamp */}
            <span className="text-xs text-muted-foreground">
              {new Date(msg.timestamp).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>

            {/* Latência */}
            {msg.debugOutput && (
              <span className="text-xs text-muted-foreground">
                {msg.debugOutput.processingMs}ms · {msg.debugOutput.iterations} iteração{msg.debugOutput.iterations !== 1 ? "ões" : ""}
              </span>
            )}

            {/* Avisos de alucinação */}
            {highWarnings.length > 0 && (
              <Badge className="bg-red-500/15 text-red-600 border-red-500/30 text-xs gap-1 py-0">
                <AlertTriangle className="w-3 h-3" />
                {highWarnings.length} alerta{highWarnings.length !== 1 ? "s" : ""} crítico{highWarnings.length !== 1 ? "s" : ""}
              </Badge>
            )}

            {/* Ferramentas usadas */}
            {(msg.debugOutput?.toolCalls?.length ?? 0) > 0 && (
              <Badge className="bg-blue-500/15 text-blue-600 border-blue-500/30 text-xs gap-1 py-0">
                <Wrench className="w-3 h-3" />
                {msg.debugOutput!.toolCalls.length} ferramenta{msg.debugOutput!.toolCalls.length !== 1 ? "s" : ""}
              </Badge>
            )}

            {/* Flagged */}
            {msg.flagged && (
              <Badge className="bg-red-500/15 text-red-600 border-red-500/30 text-xs gap-1 py-0">
                <Flag className="w-3 h-3" />
                Marcado como incorreto
              </Badge>
            )}

            {/* Botão de marcar como incorreto */}
            {!msg.flagged ? (
              <button
                onClick={() => setShowFlagForm(!showFlagForm)}
                className="text-xs text-muted-foreground hover:text-red-500 transition-colors flex items-center gap-1"
              >
                <Flag className="w-3 h-3" />
                Marcar erro
              </button>
            ) : (
              <button
                onClick={() => onUnflag(msg.id)}
                className="text-xs text-muted-foreground hover:text-green-500 transition-colors flex items-center gap-1"
              >
                <XCircle className="w-3 h-3" />
                Desmarcar
              </button>
            )}
          </div>
        )}

        {/* Formulário de flag */}
        {showFlagForm && !msg.flagged && (
          <div className="flex gap-2 w-full max-w-sm">
            <Input
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              placeholder="Por que esta resposta está errada?"
              className="h-8 text-xs"
              onKeyDown={(e) => {
                if (e.key === "Enter" && flagReason.trim()) {
                  onFlag(msg.id, flagReason.trim());
                  setShowFlagForm(false);
                  setFlagReason("");
                }
              }}
            />
            <Button
              size="sm"
              className="h-8 bg-red-500 hover:bg-red-600 text-white shrink-0"
              onClick={() => {
                if (flagReason.trim()) {
                  onFlag(msg.id, flagReason.trim());
                  setShowFlagForm(false);
                  setFlagReason("");
                }
              }}
            >
              <Flag className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 shrink-0"
              onClick={() => setShowFlagForm(false)}
            >
              ✕
            </Button>
          </div>
        )}

        {/* Motivo do flag */}
        {msg.flagged && msg.flagReason && (
          <div className="text-xs text-red-600 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-1.5 max-w-sm">
            <strong>Motivo:</strong> {msg.flagReason}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Painel de Debug ──────────────────────────────────────────────────────────

function DebugPanel({ output, messageContent }: { output: DebugOutput; messageContent: string }) {
  const [activeTab, setActiveTab] = useState<"diagnosis" | "sources" | "tools" | "prompt" | "data">("diagnosis");
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const [expandedTools, setExpandedTools] = useState<Set<number>>(new Set());
  const [showFullPrompt, setShowFullPrompt] = useState(false);

  const toggleSource = (id: string) => {
    const next = new Set(expandedSources);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpandedSources(next);
  };

  const toggleTool = (idx: number) => {
    const next = new Set(expandedTools);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    setExpandedTools(next);
  };

  const tabs = [
    { id: "diagnosis", label: "Diagnóstico", icon: <AlertCircle className="w-3.5 h-3.5" /> },
    { id: "sources", label: `Fontes (${output.sources.length})`, icon: <Database className="w-3.5 h-3.5" /> },
    { id: "tools", label: `Ferramentas (${output.toolCalls.length})`, icon: <Wrench className="w-3.5 h-3.5" /> },
    { id: "prompt", label: "System Prompt", icon: <Code2 className="w-3.5 h-3.5" /> },
    { id: "data", label: "Dados do Salão", icon: <FileText className="w-3.5 h-3.5" /> },
  ] as const;

  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex gap-1 p-2 border-b border-border/50 bg-muted/30 flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === tab.id
                ? "bg-background border border-border/50 text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-background/50"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">

          {/* ─── Diagnóstico ─── */}
          {activeTab === "diagnosis" && (
            <div className="space-y-3">
              {/* Resumo */}
              <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                <p className="text-sm leading-relaxed">{output.diagnosis}</p>
              </div>

              {/* Métricas */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2.5 rounded-lg bg-muted/20 border border-border/40 text-center">
                  <p className="text-lg font-bold text-foreground">{output.processingMs}ms</p>
                  <p className="text-xs text-muted-foreground">Latência</p>
                </div>
                <div className="p-2.5 rounded-lg bg-muted/20 border border-border/40 text-center">
                  <p className="text-lg font-bold text-foreground">{output.iterations}</p>
                  <p className="text-xs text-muted-foreground">Iterações</p>
                </div>
                <div className="p-2.5 rounded-lg bg-muted/20 border border-border/40 text-center">
                  <p className="text-lg font-bold text-foreground">{output.toolCalls.length}</p>
                  <p className="text-xs text-muted-foreground">Ferramentas</p>
                </div>
              </div>

              {/* Avisos de alucinação */}
              {output.hallucinationWarnings.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Alertas Detectados</p>
                  {output.hallucinationWarnings.map((w, i) => (
                    <div key={i} className={`p-3 rounded-lg border text-xs ${severityColor(w.severity)}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        <span className="font-semibold capitalize">{w.type.replace(/_/g, " ")}</span>
                        <Badge className={`text-xs py-0 ${severityColor(w.severity)}`}>
                          {w.severity === "high" ? "Crítico" : w.severity === "medium" ? "Atenção" : "Informativo"}
                        </Badge>
                      </div>
                      <p className="leading-relaxed">{w.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-600 text-sm">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  Nenhum alerta de alucinação detectado nesta resposta.
                </div>
              )}
            </div>
          )}

          {/* ─── Fontes ─── */}
          {activeTab === "sources" && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Fontes de dados disponíveis no contexto do agente. As marcadas como <strong>Usada</strong> foram injetadas no system prompt.
              </p>
              {output.sources.map((src) => (
                <div key={src.id} className={`rounded-lg border overflow-hidden ${src.used ? "border-border/50" : "border-border/20 opacity-60"}`}>
                  <button
                    className="w-full flex items-center gap-2 p-2.5 text-left hover:bg-muted/30 transition-colors"
                    onClick={() => toggleSource(src.id)}
                  >
                    {sourceTypeIcon(src.type)}
                    <span className="text-xs font-medium flex-1 truncate">{src.label}</span>
                    <Badge className={`text-xs py-0 shrink-0 ${src.used ? "bg-green-500/15 text-green-600 border-green-500/30" : "bg-muted text-muted-foreground"}`}>
                      {src.used ? "Usada" : "Não usada"}
                    </Badge>
                    {expandedSources.has(src.id) ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                  </button>
                  {expandedSources.has(src.id) && (
                    <div className="px-3 pb-3 pt-1 border-t border-border/30 bg-muted/10">
                      <p className="text-xs text-muted-foreground font-mono leading-relaxed whitespace-pre-wrap">{src.excerpt || "(sem conteúdo)"}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ─── Ferramentas ─── */}
          {activeTab === "tools" && (
            <div className="space-y-2">
              {output.toolCalls.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  <Wrench className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  Nenhuma ferramenta foi chamada nesta resposta.
                  <p className="text-xs mt-1">O agente respondeu apenas com base no contexto estático.</p>
                </div>
              ) : (
                output.toolCalls.map((tc, i) => (
                  <div key={i} className={`rounded-lg border overflow-hidden ${toolCallColor(tc.name)}`}>
                    <button
                      className="w-full flex items-center gap-2 p-2.5 text-left hover:opacity-80 transition-opacity"
                      onClick={() => toggleTool(i)}
                    >
                      <Wrench className="w-3.5 h-3.5 shrink-0" />
                      <span className="text-xs font-mono font-semibold flex-1">{tc.name}</span>
                      <span className="text-xs opacity-70">{tc.durationMs}ms</span>
                      {expandedTools.has(i) ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                    {expandedTools.has(i) && (
                      <div className="px-3 pb-3 pt-1 border-t border-current/20 space-y-2">
                        <div>
                          <p className="text-xs font-semibold mb-1 opacity-70">Argumentos:</p>
                          <pre className="text-xs font-mono bg-background/50 rounded p-2 overflow-auto max-h-32 whitespace-pre-wrap">
                            {JSON.stringify(tc.args, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <p className="text-xs font-semibold mb-1 opacity-70">Resultado:</p>
                          <pre className="text-xs font-mono bg-background/50 rounded p-2 overflow-auto max-h-40 whitespace-pre-wrap">
                            {(() => {
                              try { return JSON.stringify(JSON.parse(tc.result), null, 2); }
                              catch { return tc.result; }
                            })()}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* ─── System Prompt ─── */}
          {activeTab === "prompt" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">System prompt completo enviado ao modelo LLM.</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  onClick={() => setShowFullPrompt(!showFullPrompt)}
                >
                  {showFullPrompt ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showFullPrompt ? "Recolher" : "Expandir"}
                </Button>
              </div>
              <div className={`rounded-lg border border-border/50 bg-muted/10 overflow-hidden ${showFullPrompt ? "" : "max-h-64"}`}>
                <pre className="text-xs font-mono p-3 whitespace-pre-wrap leading-relaxed overflow-auto">
                  {output.systemPrompt}
                </pre>
              </div>
              {!showFullPrompt && (
                <button
                  className="text-xs text-primary hover:underline"
                  onClick={() => setShowFullPrompt(true)}
                >
                  Ver prompt completo ({output.systemPrompt.length} caracteres)
                </button>
              )}
            </div>
          )}

          {/* ─── Dados do Salão ─── */}
          {activeTab === "data" && (
            <div className="space-y-3">
              {/* Serviços */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Scissors className="w-3.5 h-3.5" />
                  Serviços Cadastrados ({output.salonData.services.length})
                </p>
                {output.salonData.services.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Nenhum serviço ativo cadastrado.</p>
                ) : (
                  <div className="space-y-1">
                    {output.salonData.services.map((s) => (
                      <div key={s.id} className="flex items-center justify-between text-xs p-2 rounded bg-muted/20 border border-border/30">
                        <span className="font-medium">{s.name}</span>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span>R$ {Number(s.price).toFixed(2)}</span>
                          <span>·</span>
                          <span>{s.durationMinutes}min</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Profissionais */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  Profissionais Ativos ({output.salonData.professionals.length})
                </p>
                {output.salonData.professionals.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Nenhum profissional ativo cadastrado.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {output.salonData.professionals.map((p) => (
                      <Badge key={p.id} variant="secondary" className="text-xs">{p.name}</Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Base de Conhecimento */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5" />
                  Base de Conhecimento ({output.salonData.knowledgeDocs.length})
                </p>
                {output.salonData.knowledgeDocs.length === 0 ? (
                  <div className="text-xs text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5">
                    Nenhum documento na Base de Conhecimento. O agente pode inventar informações sobre políticas e produtos.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {output.salonData.knowledgeDocs.map((d) => (
                      <div key={d.id} className="flex items-center justify-between text-xs p-2 rounded bg-muted/20 border border-border/30">
                        <span className="font-medium truncate">{d.name}</span>
                        <Badge className={`text-xs py-0 shrink-0 ${
                          d.status === "ready" ? "bg-green-500/15 text-green-600 border-green-500/30" :
                          d.status === "processing" ? "bg-amber-500/15 text-amber-600 border-amber-500/30" :
                          "bg-red-500/15 text-red-600 border-red-500/30"
                        }`}>
                          {d.status === "ready" ? "Pronto" : d.status === "processing" ? "Processando" : "Erro"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Componente Principal ─────────────────────────────────────────────────────

export default function AdminAgentTest() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [selectedMsgId, setSelectedMsgId] = useState<string | null>(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [showDebugDrawer, setShowDebugDrawer] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const sandboxSalonId = useMemo(() => {
    const value = new URLSearchParams(window.location.search).get("salonId");
    const parsed = value ? Number(value) : NaN;
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
  }, []);
  const salonInput = useMemo(() => sandboxSalonId ? { salonId: sandboxSalonId } : {}, [sandboxSalonId]);

  const { data: salonContext } = trpc.agentTest.getSalonContext.useQuery(salonInput);

  const sendMessageMutation = trpc.agentTest.sendMessage.useMutation({
    onSuccess: (data, variables) => {
      const assistantMsg: ChatMessage = {
        id: `msg_${Date.now()}_assistant`,
        role: "assistant",
        content: data.reply,
        timestamp: Date.now(),
        debugOutput: data as any,
      };
      setMessages((prev) => [...prev, assistantMsg]);
      setSelectedMsgId(assistantMsg.id);
    },
    onError: (e) => {
      toast.error(`Erro: ${e.message}`);
      setMessages((prev) => prev.filter((m) => m.id !== "loading"));
    },
  });

  const handleSend = () => {
    const text = input.trim();
    if (!text || sendMessageMutation.isPending) return;

    const userMsg: ChatMessage = {
      id: `msg_${Date.now()}_user`,
      role: "user",
      content: text,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");

    // Histórico de sessão (excluindo mensagem de loading)
    const sessionHistory = [...messages, userMsg]
      .filter((m) => m.id !== "loading")
      .map((m) => ({ role: m.role, content: m.content }));
    const pendingAvailabilityOffer = [...messages]
      .reverse()
      .find((message) => message.role === "assistant")
      ?.debugOutput?.pendingAvailabilityOffer;

    sendMessageMutation.mutate({
      ...salonInput,
      message: text,
      sessionHistory: sessionHistory.slice(-20), // últimas 20 mensagens
      pendingAvailabilityOffer,
    });
  };

  const handleClearChat = () => {
    setMessages([]);
    setSelectedMsgId(null);
    toast.success("Conversa limpa.");
  };

  const handleFlag = (id: string, reason: string) => {
    setMessages((prev) =>
      prev.map((m) => m.id === id ? { ...m, flagged: true, flagReason: reason } : m)
    );
    toast.success("Resposta marcada como incorreta.");
  };

  const handleUnflag = (id: string) => {
    setMessages((prev) =>
      prev.map((m) => m.id === id ? { ...m, flagged: false, flagReason: undefined } : m)
    );
  };

  // Scroll automático
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const selectedMsg = messages.find((m) => m.id === selectedMsgId);
  const flaggedCount = messages.filter((m) => m.flagged).length;

  // Ao selecionar mensagem no mobile, abrir drawer automaticamente
  const handleSelectMsg = useCallback((id: string | null) => {
    setSelectedMsgId(id);
    if (id) setShowDebugDrawer(true);
  }, []);

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] gap-0 relative">
      {/* ─── Header compacto ─── */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/50 bg-background shrink-0">
        {/* Ícone + título em linha */}
        <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
          <FlaskConical className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h1 className="text-sm font-semibold leading-tight">Teste do Agente</h1>
            <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 text-[10px] px-1.5 py-0">Sandbox</Badge>
            {sandboxSalonId && salonContext?.salon?.name && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{salonContext.salon.name}</Badge>
            )}
            {sandboxSalonId && salonContext?.instagram && (
              <Badge
                className={salonContext.instagram.connected
                  ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/30 text-[10px] px-1.5 py-0"
                  : "bg-muted text-muted-foreground border-border text-[10px] px-1.5 py-0"}
              >
                {salonContext.instagram.connected
                  ? `Instagram conectado${salonContext.instagram.username ? ` · @${salonContext.instagram.username.replace(/^@/, "")}` : ""}`
                  : "Instagram não conectado"}
              </Badge>
            )}
            {flaggedCount > 0 && (
              <Badge className="bg-red-500/15 text-red-600 border-red-500/30 text-[10px] px-1.5 py-0 gap-1">
                <Flag className="w-2.5 h-2.5" />
                {flaggedCount}
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground truncate leading-tight">
            {salonContext ? `${salonContext.services.length} serv · ${salonContext.professionals.length} prof` : "Carregando..."}
          </p>
        </div>
        {/* Botões de ação */}
        <div className="flex items-center gap-1 shrink-0">
          {/* Mobile: botão de debug */}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 md:hidden"
            onClick={() => setShowDebugDrawer(true)}
          >
            <PanelRight className="w-4 h-4" />
          </Button>
          {/* Desktop: toggle debug */}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 h-8 hidden md:flex"
            onClick={() => setShowDebugPanel(!showDebugPanel)}
            aria-label={showDebugPanel ? "Ocultar diagnóstico interno" : "Ver diagnóstico interno"}
          >
            {showDebugPanel ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            <span className="hidden lg:inline">{showDebugPanel ? "Ocultar diagnóstico" : "Ver diagnóstico interno"}</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            onClick={handleClearChat}
            disabled={messages.length === 0}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Aviso de modo teste — compacto */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border-b border-amber-500/20 text-amber-700 text-[11px] shrink-0">
        <AlertTriangle className="w-3 h-3 shrink-0" />
        <span className="truncate"><strong>Sandbox interno:</strong> o chat simula somente a resposta vista pela cliente; diagnósticos, fontes e ferramentas não são enviados. Agendamentos simulados não são gravados no banco.</span>
      </div>

      {/* ─── Corpo principal ─── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Chat */}
        <div className="flex flex-col flex-1 min-w-0">
          <ScrollArea className="flex-1 px-3 py-3">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-center gap-3 py-8">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-base">Teste o agente</h3>
                  <p className="text-xs text-muted-foreground mt-1 max-w-xs">
                    Envie uma mensagem como se fosse um cliente.
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5 justify-center max-w-sm">
                  {[
                    "Quais serviços vocês oferecem?",
                    "Qual o preço do corte?",
                    "Quero agendar para amanhã",
                    "Horários de funcionamento?",
                    "Vocês têm promoção?",
                  ].map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => setInput(suggestion)}
                      className="text-xs px-2.5 py-1 rounded-full border border-border/50 bg-muted/30 hover:bg-muted/60 transition-colors text-muted-foreground hover:text-foreground"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    onClick={() => msg.role === "assistant" && handleSelectMsg(msg.id === selectedMsgId ? null : msg.id)}
                    className={msg.role === "assistant" ? "cursor-pointer" : ""}
                  >
                    <ChatBubble
                      msg={msg}
                      onFlag={handleFlag}
                      onUnflag={handleUnflag}
                      showInternalMetadata={showDebugPanel || showDebugDrawer}
                    />
                    {msg.role === "assistant" && msg.id === selectedMsgId && (
                      <div className="ml-11 mt-1 text-xs text-primary flex items-center gap-1">
                        <ChevronRight className="w-3 h-3" />
                        <span className="hidden md:inline">Detalhes internos no painel de diagnóstico →</span>
                        <span className="md:hidden">Toque para ver diagnóstico interno →</span>
                      </div>
                    )}
                  </div>
                ))}
                {sendMessageMutation.isPending && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-secondary border border-border/50 flex items-center justify-center shrink-0">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                    <div className="bg-card border border-border/50 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">Processando...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="p-3 border-t border-border/50 bg-background shrink-0">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Mensagem como cliente..."
                className="flex-1 text-sm"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={sendMessageMutation.isPending}
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || sendMessageMutation.isPending}
                className="bg-primary text-primary-foreground shrink-0 h-10 w-10 p-0"
              >
                {sendMessageMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* ─── Painel de Debug (desktop lateral) ─── */}
        {showDebugPanel && (
          <div className="hidden md:flex w-[380px] lg:w-[420px] shrink-0 border-l border-border/50 flex-col bg-background">
            {selectedMsg?.debugOutput ? (
              <>
                <div className="px-3 py-2.5 border-b border-border/50 bg-muted/20 shrink-0">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                    <Code2 className="w-3.5 h-3.5" />
                    Diagnóstico interno — Resposta selecionada
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    "{selectedMsg.content.slice(0, 60)}{selectedMsg.content.length > 60 ? "..." : ""}"
                  </p>
                  <p className="mt-1 text-[11px] text-amber-700">Este painel não é enviado à cliente.</p>
                </div>
                <DebugPanel output={selectedMsg.debugOutput} messageContent={selectedMsg.content} />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-6 gap-3">
                <Code2 className="w-10 h-10 text-muted-foreground/30" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Nenhuma resposta selecionada</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Clique em uma resposta do agente no chat para ver os detalhes de rastreabilidade.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Drawer de Debug (mobile) ─── */}
      {showDebugDrawer && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowDebugDrawer(false)}
          />
          {/* Painel deslizante da direita */}
          <div className="absolute right-0 top-0 bottom-0 w-[90vw] max-w-sm bg-background border-l border-border flex flex-col shadow-xl">
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-border/50 bg-muted/20 shrink-0">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                <Code2 className="w-3.5 h-3.5" />
                Painel de Debug
              </p>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setShowDebugDrawer(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            {selectedMsg?.debugOutput ? (
              <>
                <div className="px-3 py-2 border-b border-border/30 bg-muted/10 shrink-0">
                  <p className="text-xs text-muted-foreground truncate">
                    "{selectedMsg.content.slice(0, 50)}{selectedMsg.content.length > 50 ? "..." : ""}"
                  </p>
                </div>
                <div className="flex-1 overflow-hidden">
                  <DebugPanel output={selectedMsg.debugOutput} messageContent={selectedMsg.content} />
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center flex-1 text-center p-6 gap-3">
                <Code2 className="w-10 h-10 text-muted-foreground/30" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Nenhuma resposta selecionada</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Toque em uma resposta do agente no chat para ver os detalhes.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
